import React, { useState } from "react";
import * as Icons from "lucide-react";
import {
  ExternalLink,
  FileText,
  Download,
  ChevronDown,
  CheckCircle2,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  workplaces,
  softSkills,
  technicalSkills,
  formationKeyInfo,
  dispensations,
  careerPaths,
  formationDocuments,
} from "../mock";

export default function LaFormation() {
  const [showDispenses, setShowDispenses] = useState(false);
  const [expandedCareer, setExpandedCareer] = useState(0);

  return (
    <section id="la-formation" className="py-20 lg:py-28 relative overflow-hidden">
      <div className="blob w-[420px] h-[420px] bg-[#2c7a80]/10 top-40 -left-32" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">La formation</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-[#0e3a3f] leading-[1.05] mt-3 title-underline">
              Le CAP Accompagnant<br />Éducatif Petite Enfance.
            </h2>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <p className="text-[#0e3a3f]/75 leading-relaxed">
              Une formation complète et exigeante, préparée chez nous en <strong>seulement 9 mois</strong>, accessible à partir d'un <strong>niveau 3ème</strong>. Depuis 2017, le CAP Petite Enfance est devenu le CAP AEPE.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={formationDocuments.rncp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2c7a80] hover:text-[#0e3a3f] transition-colors group"
              >
                <ExternalLink className="w-4 h-4" />
                Fiche RNCP
                <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href={formationDocuments.programmeCap}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2c7a80] hover:text-[#0e3a3f] transition-colors group"
              >
                <FileText className="w-4 h-4" />
                Programme 2026
                <Download className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href={formationDocuments.ficheCap}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2c7a80] hover:text-[#0e3a3f] transition-colors group"
              >
                <FileText className="w-4 h-4" />
                Fiche 2025
                <Download className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
        </div>

        {/* Introduction card */}
        <div className="bg-white border border-[#e6ded0] rounded-3xl overflow-hidden mb-14 grid md:grid-cols-5">
          <div className="md:col-span-3 p-8 lg:p-10">
            <div className="flex items-center gap-2 text-[#e8a04d] mb-3">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest font-semibold">Qu'est-ce que le CAP AEPE ?</span>
            </div>
            <p className="text-lg text-[#0e3a3f]/80 leading-relaxed">
              Le CAP (Certificat d'Aptitude Professionnelle) est un <strong>diplôme d'État</strong> qui a pour mission de former les futurs professionnels de l'accueil et de la garde de jeunes enfants, des premiers mois de leur vie jusqu'à l'âge de 6 ans. À l'issue des 9 mois de formation, les stagiaires passent leurs examens finaux dans un centre d'examen de l'Éducation Nationale.
            </p>
          </div>
          <div className="md:col-span-2 relative min-h-[280px] md:min-h-full">
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_formation-capp/artifacts/rs20jw1w_71c929ca-d33c-464d-9838-322b836898e8.JPG"
              alt="Petite enfance"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center 25%" }}
            />
          </div>
        </div>

        {/* Workplaces */}
        <div className="mb-16">
          <h3 className="font-display text-2xl md:text-3xl font-medium text-[#0e3a3f] mb-6">
            Où exercer avec un CAP AEPE&nbsp;?
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {workplaces.map((w) => {
              const Icon = Icons[w.icon] || Icons.Star;
              return (
                <div
                  key={w.label}
                  className="group bg-white border border-[#e6ded0] rounded-2xl p-5 text-center hover-lift"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#2c7a80]/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-[#2c7a80] transition-colors">
                    <Icon className="w-6 h-6 text-[#2c7a80] group-hover:text-white transition-colors" />
                  </div>
                  <div className="text-sm font-semibold text-[#0e3a3f] leading-tight">{w.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Qualities & Skills */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <div className="bg-[#f5f1e8]/60 rounded-3xl p-8 border border-[#e6ded0]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-[#e8a04d]/20 flex items-center justify-center">
                <Icons.HeartHandshake className="w-5 h-5 text-[#e8a04d]" />
              </div>
              <h3 className="font-display text-2xl font-medium text-[#0e3a3f]">Qualités relationnelles</h3>
            </div>
            <ul className="grid sm:grid-cols-2 gap-2">
              {softSkills.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-[#0e3a3f]/80">
                  <CheckCircle2 className="w-4 h-4 text-[#2c7a80] mt-0.5 flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#0e3a3f] rounded-3xl p-8 text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#2c7a80]/30 blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Icons.Wrench className="w-5 h-5 text-[#e8a04d]" />
                </div>
                <h3 className="font-display text-2xl font-medium">Compétences techniques</h3>
              </div>
              <ul className="space-y-2.5">
                {technicalSkills.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-sm text-white/85">
                    <CheckCircle2 className="w-4 h-4 text-[#e8a04d] mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Key info cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {formationKeyInfo.map((info) => {
            const Icon = Icons[info.icon] || Icons.Info;
            return (
              <div
                key={info.title}
                className="bg-white border border-[#e6ded0] rounded-2xl p-6 hover-lift"
              >
                <div className="w-11 h-11 rounded-xl bg-[#2c7a80]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#2c7a80]" />
                </div>
                <h4 className="font-display text-lg font-medium text-[#0e3a3f] mb-3">{info.title}</h4>
                <ul className="space-y-1.5">
                  {info.items.map((it) => (
                    <li key={it} className="flex items-start gap-1.5 text-xs text-[#0e3a3f]/75">
                      <span className="mt-1 w-1 h-1 rounded-full bg-[#e8a04d] flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Dispensations - collapsible */}
        <div className="mb-16">
          <button
            onClick={() => setShowDispenses((v) => !v)}
            className={`w-full text-left rounded-2xl border transition-colors ${
              showDispenses ? "bg-white border-[#2c7a80] shadow-lg shadow-[#0e3a3f]/5" : "bg-white/60 border-[#e6ded0] hover:border-[#2c7a80]/40"
            }`}
          >
            <div className="p-6 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold mb-1">
                  {dispensations.subtitle}
                </div>
                <h4 className="font-display text-xl md:text-2xl font-medium text-[#0e3a3f]">
                  {dispensations.title}
                </h4>
              </div>
              <div
                className={`flex-shrink-0 w-11 h-11 rounded-full border flex items-center justify-center transition-all ${
                  showDispenses ? "bg-[#2c7a80] border-[#2c7a80] text-white rotate-180" : "border-[#e6ded0] text-[#2c7a80]"
                }`}
              >
                <ChevronDown className="w-5 h-5" />
              </div>
            </div>
          </button>
          <div
            className={`grid transition-all duration-300 ease-out ${
              showDispenses ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <div className="bg-white border border-[#e6ded0] rounded-2xl p-8">
                <p className="text-[#0e3a3f]/80 leading-relaxed mb-5">{dispensations.intro}</p>
                <ul className="grid md:grid-cols-2 gap-2 mb-6">
                  {dispensations.diplomas.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-[#0e3a3f]/85">
                      <CheckCircle2 className="w-4 h-4 text-[#2c7a80] mt-0.5 flex-shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-[#f5f1e8]/60 border border-[#e6ded0] rounded-xl p-5">
                  <div className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold mb-2">Diplômes étrangers</div>
                  <p className="text-sm text-[#0e3a3f]/80 leading-relaxed">{dispensations.foreign}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Career paths */}
        <div>
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-8">
            <div className="lg:col-span-8">
              <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">Poursuites d'études</span>
              <h3 className="font-display text-3xl md:text-4xl font-medium text-[#0e3a3f] mt-2">
                Et après le CAP AEPE&nbsp;?
              </h3>
            </div>
            <div className="lg:col-span-4">
              <p className="text-sm text-[#0e3a3f]/70">
                8 formations et concours accessibles après l'obtention de votre CAP AEPE pour évoluer dans les métiers de la petite enfance et du médico-social.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {careerPaths.map((c, i) => {
              const isOpen = expandedCareer === i;
              return (
                <div
                  key={c.title}
                  className={`bg-white rounded-2xl border transition-colors ${
                    isOpen ? "border-[#2c7a80] shadow-lg shadow-[#0e3a3f]/5" : "border-[#e6ded0]"
                  }`}
                >
                  <button
                    onClick={() => setExpandedCareer(isOpen ? -1 : i)}
                    className="w-full text-left p-6 flex items-center justify-between gap-4 group"
                  >
                    <div className="flex items-center gap-5 flex-1 min-w-0">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-display text-sm font-semibold ${
                        isOpen ? "bg-[#2c7a80] text-white" : "bg-[#2c7a80]/10 text-[#2c7a80]"
                      }`}>
                        {(i + 1).toString().padStart(2, "0")}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-display text-lg md:text-xl font-medium text-[#0e3a3f] group-hover:text-[#2c7a80] transition-colors">{c.title}</h4>
                        <div className="text-xs text-[#0e3a3f]/60 mt-0.5">{c.subtitle}</div>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[#0e3a3f]/50 font-semibold">Durée</div>
                        <div className="text-sm font-medium text-[#0e3a3f]">{c.duration}</div>
                      </div>
                      <div className="h-8 w-px bg-[#e6ded0]" />
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[#0e3a3f]/50 font-semibold">Niveau</div>
                        <div className="text-sm font-medium text-[#0e3a3f]">{c.level}</div>
                      </div>
                    </div>
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full border transition-all ${
                        isOpen ? "bg-[#2c7a80] border-[#2c7a80] text-white rotate-180" : "border-[#e6ded0] text-[#2c7a80]"
                      } flex items-center justify-center`}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 pl-24">
                        {/* Mobile info */}
                        <div className="sm:hidden flex gap-6 mb-4 pb-4 border-b border-[#e6ded0]">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#0e3a3f]/50 font-semibold">Durée</div>
                            <div className="text-sm font-medium text-[#0e3a3f]">{c.duration}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#0e3a3f]/50 font-semibold">Niveau</div>
                            <div className="text-sm font-medium text-[#0e3a3f]">{c.level}</div>
                          </div>
                        </div>
                        <ul className="grid sm:grid-cols-2 gap-2">
                          {c.highlights.map((h) => (
                            <li key={h} className="flex items-start gap-2 text-sm text-[#0e3a3f]/80">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e8a04d] flex-shrink-0" />
                              <span>{h}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-center gap-4 justify-center">
          <Button
            asChild
            size="lg"
            className="bg-[#2c7a80] hover:bg-[#245e63] text-white rounded-full px-7 h-12 btn-primary"
          >
            <a href="#contact">Candidater à la formation</a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-2 border-[#0e3a3f]/15 hover:border-[#2c7a80] hover:bg-transparent text-[#0e3a3f] rounded-full px-7 h-12"
          >
            <a href={formationDocuments.programmeCap} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4 mr-2" />
              Télécharger le programme
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
